import { useSearchParams, Link } from "react-router-dom";
import { useEffect } from "react";
import {
  Sparkle, Compass, Star, Crown, ArrowRight, CheckCircle, Brain, ListChecks, Gift,
} from "@phosphor-icons/react";

const features = [
  "Descreve a tua vida de sonho — a IA sugere 8-10 sonhos concretos",
  "Cada sonho com data, valor, prémio e consequência",
  "Foto do sonho e do prémio para manter a visão viva",
  "Plano de acção gerado por IA, com tarefas calendarizáveis",
  "Dream Board visual e top de Sonhadores",
  "Emails diários motivacionais + alerta de tarefas pendentes",
];

const testimonials = [
  { name: "Ana C.", role: "Empreendedora", text: "Em 90 dias passei de \"quero abrir uma empresa\" a faturar o primeiro cliente. O plano de IA fez o que 3 livros de produtividade não fizeram." },
  { name: "Pedro M.", role: "Estudante", text: "Comecei com uma média de 12. Hoje tenho 17. As tarefas calendarizadas + os emails de manhã fizeram a diferença." },
  { name: "Sofia R.", role: "Mãe de 2", text: "Pensei que sonhar era para quando tivesse \"mais tempo\". O Dreams mostrou-me que basta começar com 20 minutos." },
];

const faq = [
  { q: "Posso cancelar quando quiser?", a: "Sim. Cancelas a renovação automática a qualquer momento e manténs acesso até ao fim do período já pago." },
  { q: "Como é cobrado?", a: "Por EasyPay: Multibanco (referência), MB Way (telemóvel) ou cartão (página segura)." },
  { q: "A IA usa os meus dados?", a: "A IA processa apenas o que escreves na app (vida de sonho, sonhos, tarefas). Não treinamos modelos com os teus dados." },
  { q: "Posso convidar amigos?", a: "Sim — por cada amigo que subscreva via o teu link, ganhas +30 dias grátis." },
];

const COOKIE_TTL_DAYS = 30;
function setReferCookie(referId) {
  const expires = new Date(Date.now() + COOKIE_TTL_DAYS * 86400 * 1000).toUTCString();
  document.cookie = `REFER=${encodeURIComponent(referId)}; path=/; expires=${expires}; SameSite=Lax`;
}

export default function DreamsLandingPublic() {
  const [params] = useSearchParams();
  const refer = params.get("REFER");

  // Persiste o REFER em cookie para honrar a convenção do registo IHTP
  useEffect(() => {
    if (refer && /^\d+$/.test(refer)) setReferCookie(refer);
  }, [refer]);

  const ctaPath = refer ? `/dreams/subscribe?REFER=${encodeURIComponent(refer)}` : "/dreams/subscribe";
  // Para utilizadores ainda não registados no site IHTP, oferecemos o registo IHTP
  // com REFER preservado (cookie + query). O register_client no backend lê este REFER.
  const registerPath = refer ? `/register?REFER=${encodeURIComponent(refer)}` : "/register";

  return (
    <div className="dreams-root min-h-screen bg-[#FAF8F3] text-[#0F1829] relative overflow-hidden">
      {/* Aurora */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-[#0F1829]/25 blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-[#978D78]/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#6F4F1D]/15 blur-3xl" />
      </div>

      <header className="relative z-10 max-w-5xl mx-auto pt-8 px-6 flex items-center justify-between">
        <Link to="/dreams" className="flex items-center gap-2 group" data-testid="landing-logo">
          <Sparkle size={20} weight="fill" className="text-[#6F4F1D] group-hover:rotate-12 transition-transform" />
          <span className="text-base font-semibold tracking-tight">IHTP <span className="text-[#6F4F1D]">Dreams</span></span>
        </Link>
        <Link to="/login" className="text-xs text-[#978D78] hover:text-white">Já tenho conta →</Link>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-12 space-y-16">
        {/* Hero */}
        <section>
          {refer && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6F4F1D]/10 border border-[#6F4F1D]/25 text-[11px] text-[#6F4F1D] font-medium mb-6" data-testid="landing-refer-badge">
              <Gift size={12} weight="fill" /> Foste convidado por um sonhador
            </div>
          )}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight max-w-3xl">
            O que <span className="text-[#6F4F1D]">sonhas</span>
            <br />
            <span className="bg-gradient-to-r from-amber-300 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">tornar realidade?</span>
          </h1>
          <p className="text-lg text-[#4B5563] max-w-xl mt-6 leading-relaxed">
            Transforma a tua visão de vida ideal numa lista de sonhos concretos — com prazos,
            prémios e um plano de acção feito à tua medida pela IA. <b>Experimenta 3 meses grátis</b> — depois desde 5€/mês.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to={ctaPath} data-testid="landing-cta-sub"
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] font-semibold text-sm hover:shadow-[0_0_40px_rgba(245,158,11,0.4)] transition-all">
              Começar grátis — 3 meses
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href={registerPath} data-testid="landing-cta-register"
               className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#E6E1D6] text-sm font-medium hover:bg-white">
              Registar conta IHTP grátis
            </a>
          </div>
        </section>

        {/* Steps */}
        <section className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Compass, title: "Define", text: "Descreve a tua vida ideal numa frase." },
            { icon: Sparkle, title: "Gera",   text: "A IA propõe sonhos concretos com prazos." },
            { icon: ListChecks, title: "Concretiza", text: "Plano, tarefas, pontos e prémios." },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-[#E6E1D6] bg-white/[0.02] p-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6F4F1D]/20 to-fuchsia-500/20 flex items-center justify-center mb-4">
                <s.icon size={20} weight="fill" className="text-[#6F4F1D]" />
              </div>
              <h3 className="text-lg font-semibold">{s.title}</h3>
              <p className="text-sm text-[#4B5563] mt-1">{s.text}</p>
            </div>
          ))}
        </section>

        {/* Features */}
        <section className="rounded-3xl border border-[#E6E1D6] bg-gradient-to-br from-violet-900/20 to-fuchsia-900/10 p-8">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Crown size={22} weight="fill" className="text-[#6F4F1D]" /> O que vais ter
          </h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-[#0F1829]">
                <CheckCircle size={16} weight="fill" className="text-[#6F4F1D] mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </section>

        {/* Testimonials */}
        <section>
          <h2 className="text-2xl font-semibold mb-5 flex items-center gap-2">
            <Star size={22} weight="fill" className="text-[#6F4F1D]" /> Sonhadores que conseguiram
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <article key={i} className="rounded-2xl border border-[#E6E1D6] bg-white/[0.02] p-5" data-testid={`testi-${i}`}>
                <p className="text-sm text-[#0F1829] italic">&ldquo;{t.text}&rdquo;</p>
                <p className="text-xs text-[#6F4F1D] mt-4 font-semibold">{t.name}</p>
                <p className="text-[11px] text-[#978D78]">{t.role}</p>
              </article>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-2xl font-semibold mb-5 flex items-center gap-2">
            <Brain size={22} weight="fill" className="text-[#6F4F1D]" /> Perguntas frequentes
          </h2>
          <div className="space-y-3">
            {faq.map((f, i) => (
              <details key={i} className="rounded-xl border border-[#E6E1D6] bg-white/[0.02] p-4" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-sm">{f.q}</summary>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="rounded-3xl bg-gradient-to-br from-amber-500/15 to-fuchsia-500/15 border border-[#6F4F1D]/25 p-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold">Começa hoje. <span className="text-[#6F4F1D]">5€</span> mudam tudo.</h2>
          <p className="text-[#4B5563] mt-3 max-w-lg mx-auto text-sm">Cancela quando quiseres. Sem fidelização.</p>
          <Link to={ctaPath} data-testid="landing-cta-final"
                className="inline-flex items-center gap-2 mt-6 px-7 py-3 rounded-full bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] font-semibold text-sm">
            Subscrever IHTP Dreams <ArrowRight size={15} weight="bold" />
          </Link>
        </section>
      </main>
    </div>
  );
}
